import {
  Input,
  Tooltip,
  TooltipProps,
} from "@cappelendamm/design-system/react";
import classNames from "classnames";
import {
  FieldError,
  FieldValues,
  Path,
  UseFormRegister,
  Validate,
} from "react-hook-form";
import { HTMLInputAutoCompleteAttribute, JSX } from "react";

type HTMLInputModeAttribute =
  | "none"
  | "text"
  | "tel"
  | "url"
  | "email"
  | "numeric"
  | "decimal"
  | "search"
  | undefined;

type ValidationWithMessage<T> = {
  value: T;
  message: string;
};

type InputFieldTypes<T extends FieldValues> = {
  register: UseFormRegister<T>;
  name: Path<T>;
  error?: FieldError;
  label?: string;
  className?: string;
  inputClassName?: string;
  type?: string;
  inputMode?: HTMLInputModeAttribute;
  requiredMessage?: string;
  maxLength?: ValidationWithMessage<number>;
  pattern?: ValidationWithMessage<RegExp>;
  validate?: Validate<any, T>;
  autoComplete?: HTMLInputAutoCompleteAttribute;
  setValueAs?: (value: any) => any;
  tooltip?: TooltipProps;
  disabled?: boolean;
  placeholder?: string;
  sensitive?: boolean;
  inputProps?: JSX.IntrinsicElements["input"];
  onBlur?: JSX.IntrinsicElements["input"]["onBlur"];
};

export const InputField = <T extends FieldValues>({
  name,
  label,
  register,
  error,
  className,
  inputClassName,
  type,
  inputMode,
  requiredMessage,
  maxLength,
  pattern,
  validate,
  setValueAs,
  autoComplete,
  tooltip,
  disabled,
  placeholder,
  sensitive,
  inputProps,
  onBlur,
}: InputFieldTypes<T>) => {
  const required: ValidationWithMessage<boolean> | undefined = requiredMessage
    ? { value: true, message: requiredMessage }
    : undefined;

  const { onBlur: registerOnBlur, ...registerProps } = register(name, {
    required,
    maxLength,
    pattern,
    setValueAs,
    validate,
    disabled,
  });

  return (
    <div className={classNames("grid gap-1", className)}>
      <label htmlFor={name}>{label}</label>
      <div className="relative">
        <Input
          id={name}
          className={classNames(
            "rr-block w-full",
            {
              error: !!error?.type,
            },
            inputClassName,
          )}
          type={type}
          inputMode={inputMode}
          {...registerProps}
          onBlur={(event) => {
            registerOnBlur(event);
            onBlur?.(event);
          }}
          autoComplete={autoComplete}
          placeholder={placeholder}
          sensitive={sensitive}
          {...inputProps}
        />
        {tooltip && (
          <Tooltip
            {...tooltip}
            className="absolute top-2 right-2"
            id={`tooltip-${name}`}
          />
        )}
      </div>
      {error && (
        <span className="text-base text-status-error-500">{error.message}</span>
      )}
    </div>
  );
};
